// ── Stock coverage for a reservation line ───────────────────────────────────
//
// How well can one component line be met? Answered from three numbers: what is
// free in stock, what is incoming on a PO and not already promised elsewhere,
// and what this line needs.
//
// The classification and the colours were previously written twice in
// ReservationDetailPage - once as a chain of returns building Tailwind classes,
// once as a hand-written legend listing those same colours with labels. Nothing
// tied them together, so a change to one silently disagreed with the other.

export type StockCoverage =
  /** Free stock alone covers the line. */
  | 'in-stock'
  /** Nothing free, but incoming stock covers it in full. */
  | 'on-order'
  /** Some free stock, and incoming covers the shortfall. */
  | 'partial-covered'
  /** Some free stock, and incoming does not close the gap. */
  | 'partial-uncovered'
  /** No free stock and not enough incoming. */
  | 'unavailable';

/**
 * Order matters: each case assumes the ones above it did not match.
 *
 * `available` can be negative when stock has been oversold, which is why the
 * partial cases test `available > 0` rather than trusting the subtraction.
 */
export function classifyStockCoverage(
  available: number,
  onOrder: number,
  required: number
): StockCoverage {
  if (available >= required) return 'in-stock';
  if (available <= 0 && available + onOrder >= required) return 'on-order';
  if (available > 0 && available + onOrder >= required) return 'partial-covered';
  if (available > 0) return 'partial-uncovered';
  return 'unavailable';
}

/** Row background for each coverage state. */
export const STOCK_COVERAGE_CLASS: Record<StockCoverage, string> = {
  'in-stock':          'bg-background',
  'on-order':          'bg-orange-200',
  'partial-covered':   'bg-purple-200',
  'partial-uncovered': 'bg-amber-200',
  'unavailable':       'bg-red-200',
};

/** Border for the legend swatches - the row backgrounds carry no border. */
export const STOCK_COVERAGE_SWATCH_BORDER: Record<StockCoverage, string> = {
  'in-stock':          'border-border',
  'on-order':          'border-orange-300',
  'partial-covered':   'border-purple-300',
  'partial-uncovered': 'border-amber-300',
  'unavailable':       'border-red-300',
};

export const STOCK_COVERAGE_LABEL: Record<StockCoverage, string> = {
  'in-stock':          'In stock',
  'on-order':          'On order',
  'partial-covered':   'Partial + on order',
  'partial-uncovered': 'Partial, gap uncovered',
  'unavailable':       'Unavailable',
};

/** Legend order, worst-covered last. */
export const STOCK_COVERAGE_ORDER: StockCoverage[] = [
  'in-stock',
  'on-order',
  'partial-covered',
  'partial-uncovered',
  'unavailable',
];
